import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

export const REFRESH_TOKEN_STORAGE_KEY = 'tms-refresh-token';

let currentAccessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;
let onRefreshFailure: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  currentAccessToken = token;
}

export function getAccessToken(): string | null {
  return currentAccessToken;
}

export function registerRefreshFailureHandler(handler: (() => void) | null): void {
  onRefreshFailure = handler;
}

export const apiClient = axios.create({
  baseURL: '',
  withCredentials: false,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (currentAccessToken && !config.headers['Authorization']) {
    config.headers.set('Authorization', `Bearer ${currentAccessToken}`);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    const url = original.url ?? '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      const newAccessToken = await rotateTokens();
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>)['Authorization'] = `Bearer ${newAccessToken}`;
      return apiClient.request(original);
    } catch (refreshErr) {
      setAccessToken(null);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      onRefreshFailure?.();
      return Promise.reject(refreshErr);
    }
  },
);

async function rotateTokens(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!storedRefresh) {
      throw new Error('No refresh token available');
    }
    const res = await axios.post<{ accessToken: string; refreshToken: string }>(
      '/api/web/v1/auth/refresh',
      { refreshToken: storedRefresh },
    );
    setAccessToken(res.data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, res.data.refreshToken);
    return res.data.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}
